import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';


// Services
import LocalStorageService from '../services/LocalStorageService';
import TimetableService from '../services/TimetableService';
import OrganisationTagService from '../services/OrganisationTagService';
import ClassService from '../services/ClassService';
import NotificationService from '../services/NotificationService';

// Grommet
import { ResponsiveContext } from 'grommet'
import { FEATURE_TIMETABLE_ENABLED } from '../env.js'

// Appt Components
import Anchor from '../components/simple/anchor/Anchor';
import Box from '../components/simple/box/Box';
import Text from '../components/simple/text/Text';
import { v4 as uuidv4 } from 'uuid';


// Appt Widgets
import moment from 'moment';
import BreadcrumbBar from './shared/BreadcrumbBar';
import ClickerGroups from './shared/ClickerGroups'
import ActionButton from '../components/compound/actionButton/ActionButton';
import ResponsiveGrid from '../components/compound/responsiveGrid/ResponsiveGrid';



function Class(props) {

  const params = useParams();
  const navigate = useNavigate()

  const classId = params.classId;

  const activeOrg = LocalStorageService.get('activeOrg');

  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);

  const [classData, setClassData] = useState(null);

  const [quickBehaviourGroups, setQuickBehaviourGroups] = useState([])


  const [requestId, setRequestId] = useState(uuidv4());
  const [clickerLoading, setClickerLoading] = useState(false);

  useEffect(() => {
    const apiToken = LocalStorageService.get('apiToken');
    if (apiToken && activeOrg) {
      void OrganisationTagService.getActiveQuickBehaviourGroups(apiToken, activeOrg).then((response) => {
        setQuickBehaviourGroups(response)
      })
    }
  }, [])

  useEffect(() => {
    if (FEATURE_TIMETABLE_ENABLED !== 'true') {
      void navigate('/');
    }
  }, []);

  useEffect(() => {
    const fetchClassData = async () => {
      const apiToken = LocalStorageService.get('apiToken');
      const data = await TimetableService.getClassById(apiToken, classId);
      if (data) {
        setClassData(data.class);

        let sortedStudents = (data.students || []).sort((a, b) => (a.firstName + a.lastName).localeCompare(b.firstName + b.lastName));
        setStudents(sortedStudents);

        let sortedStaff = (data.staff || []).sort((a, b) => (a.firstName + a.lastName).localeCompare(b.firstName + b.lastName));
        setStaff(sortedStaff);
      }
    }
    void fetchClassData();
  }, [classId]);

  const postClickerForClass = async (clickerType) => {

    setClickerLoading(true);

    const apiToken = LocalStorageService.get('apiToken');
    if (apiToken && classId) {
      let response = await ClassService.postClickerForClass(apiToken, classId, clickerType, requestId);
      setClickerLoading(false);
      setRequestId(uuidv4());

      if (!response) {
        NotificationService.error(`Failed to post "${clickerType}"`);
        return;
      }
      if (response.success) {
        NotificationService.success(`"${clickerType}" recorded successfully`);
      } else {
        NotificationService.error(`Failed to post "${clickerType}"`);
      }
    }
  }


  return (
     <ResponsiveContext.Consumer>
      {(size) => {
        return (
          <Box margin={{ bottom: 'large' }}>
        <BreadcrumbBar
              title={"Class Overview"}
          path={<> <Anchor href="/">BehaviourSmart</Anchor></>}
        />




            <Box direction='row' gap='small' round='small' flex='grow' margin={{ top: 'small', bottom: 'small' }}>

              <ActionButton
                label="< Back"
                onClick={() => navigate('/timetable/' + moment(classData.startTime).format('YYYY-MM-DD'))}
                width={100}
              />

              <Box background='white' direction='row-responsive' round='small' flex='grow' justify='between' pad={{ horizontal: 'medium', vertical: 'small' }}>
                <Text size="xlarge" weight="bold" color='secondary'>
                  {classData ? `${classData.lesson}` : 'Class Details'}
                </Text>
              </Box>
            </Box>







            <Box
              background="white"
              round="small"
              width="xlarge"
            >

              <ResponsiveGrid
                rows={['auto']}
                columns={{
                  small: ['auto'],
                  medium: ['auto', 'auto'],
                  large: ['40%', '40%'],
                  xlarge: ['40%', '40%'],
                }}
                gap="small"
                pad={{ horizontal: 'medium', vertical: 'small' }}
              >

                <Box gap='none'>
                  <Text weight='bold'>Date</Text>
                  <Text size="large">{moment(classData?.startTime).format('ddd, MMM Do YYYY')}</Text>
                </Box>

                <Box gap='none'>
                  <Text weight='bold'>Time</Text>
                  <Text size="large">
                    {moment(classData?.startTime).format('HH:mm')} - {moment(classData?.endTime).format('HH:mm')}
                  </Text>
                </Box>

                <Box gap='none'>
                  <Text weight='bold'>Room</Text>
                  <Text size="large">{classData ? `${classData.room}` : ''}</Text>
                </Box>

                <Box gap='none'>
                  <Text weight='bold'>Period</Text>
                  <Text size="large">{classData ? `${classData.period}` : ''}</Text>
                </Box>

                <Box gap='none'>
                  <Text weight='bold'>Year Group</Text>
                  <Text size="large">{classData ? `${classData.yearGroup}` : ''}</Text>
                </Box>

                <Box gap='none'>
                  <Text weight='bold'>Lesson</Text>
                  <Text size="large">{classData ? `${classData.lesson}` : ''}</Text>
                </Box>


                <Box>
                  <Text weight='bold'>Staff</Text>
                  {staff?.map(s => <Text key={s.id} size="large" primary><Anchor target='_blank' color='primary' href={`/user/${s.id}/profile`}>{s.firstName + ' ' + s.lastName}</Anchor></Text>)}
                </Box>


                <Box>
                  <Text weight='bold'>Students</Text>
                  {students?.map(s => <Text key={s.id} size="large" primary><Anchor target='_blank' href={`/serviceuser/${s.id}/profile`} color='primary'>{s.firstName + ' ' + s.lastName}</Anchor></Text>)}
                </Box>


              </ResponsiveGrid>






            </Box>


            <ClickerGroups
              groups={quickBehaviourGroups}
              size={size}
              title="Whole Class Behaviour Clicker"
              onClickBehaviour={behaviour => clickerLoading ? null : postClickerForClass(behaviour)}
            />
          </Box>
    )}}
    </ResponsiveContext.Consumer>
  );
}

export default Class;
